import { config } from "../config.js";
import { idToken } from "../auth/amplify.js";

/**
 * A failure the API described. `code` is machine-readable; `message` is
 * already written for a person and is safe to show, because the handler
 * deliberately keeps diagnostics server-side.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }

  /** True when the caller may not do this - as opposed to it having gone wrong. */
  get isRefusal(): boolean {
    return this.status === 401 || this.status === 403 || this.status === 404;
  }

  /** True when someone else changed the record first. Worth a reload, not a retry. */
  get isConflict(): boolean {
    return this.status === 409;
  }
}

interface RequestOptions {
  readonly method?: "GET" | "POST" | "PUT" | "DELETE";
  readonly body?: unknown;
  readonly query?: Record<string, string | number | boolean | undefined>;
}

function buildUrl(path: string, query: RequestOptions["query"]): string {
  const url = new URL(config.apiEndpoint + path);

  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }

  return url.toString();
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await idToken();

  if (token === undefined) {
    throw new ApiError(401, "unauthenticated", "Your session has ended. Please sign in again.");
  }

  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? "GET",
    headers: {
      authorization: `Bearer ${token}`,
      ...(options.body === undefined ? {} : { "content-type": "application/json" }),
    },
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text === "" ? {} : safeJson(text);

  if (!response.ok) {
    // The gateway's own refusals (an expired or missing token) are not shaped
    // like the handler's, so both have to be understood.
    const error = (parsed as { error?: { code?: string; message?: string; requestId?: string } })
      .error;

    throw new ApiError(
      response.status,
      error?.code ?? "request_failed",
      error?.message ?? fallbackMessage(response.status),
      error?.requestId,
    );
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function fallbackMessage(status: number): string {
  switch (status) {
    case 401:
      return "Your session has ended. Please sign in again.";
    case 403:
      return "You do not have access to this.";
    case 404:
      return "Not found.";
    default:
      return "Something went wrong. Please try again.";
  }
}

/**
 * Uploads a file straight to S3 with a presigned URL.
 *
 * Deliberately not routed through `api` - it carries no bearer token, because
 * the signature in the URL is the authorisation, and sending our Cognito token
 * to S3 would leak it outside the API for no benefit. The headers must match
 * what was signed exactly or S3 refuses the request.
 */
export async function uploadToS3(
  url: string,
  headers: Record<string, string>,
  file: Blob,
): Promise<void> {
  const response = await fetch(url, { method: "PUT", headers, body: file });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      "upload_failed",
      "The file could not be uploaded. It may have taken too long — please try again.",
    );
  }
}
